'use client';
import { checkType } from '@/common/ultils';
import get from 'lodash/get';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState, useEffect, useCallback } from 'react';

const FeaturedEvent = ({ featuredEvents, deviceType }) => {
  const t = useTranslations('');
  const posts = get(featuredEvents, 'results', []);
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback(
    (index) => {
      if (isAnimating || index === current) return;
      setIsAnimating(true);
      setCurrent(index);
      setTimeout(() => setIsAnimating(false), 600);
    },
    [isAnimating, current],
  );

  const prev = () => goTo((current - 1 + posts.length) % posts.length);
  const next = useCallback(() => goTo((current + 1) % posts.length), [current, goTo, posts.length]);

  useEffect(() => {
    if (posts.length <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, posts.length]);

  if (!posts.length) return null;

  return (
    <div className="relative w-full h-[420px] md:h-[480px] rounded-xl overflow-hidden shadow-lg group">
      {/* Slides */}
      {posts.map((post, index) => {
        const imageUrl = checkType(deviceType === 'mobile' ? 'mobile' : 'tablet', post, -1);
        return (
          <div
            key={post.slug}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
            {/* Background Image */}
            <Image src={imageUrl} alt={post.title} fill className="object-cover" priority={index === 0} />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

            {/* Content */}
            <Link href={`${post?.menuId?.slug}/${post.slug}`} className="absolute inset-0 z-10 flex flex-col justify-end p-5 md:p-8">
              <span className="inline-block self-start bg-[#b71c1c] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded mb-3">
                {t('home.highlightNews')}
              </span>
              <h2 className="text-white font-bold text-base md:text-2xl leading-snug max-w-2xl line-clamp-3 drop-shadow-lg">{post.title}</h2>
              {post.publishedAt && (
                <p className="text-white/70 text-xs mt-2 flex items-center gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {new Date(post.publishedAt).toLocaleDateString('vi-VN')}
                </p>
              )}
            </Link>
          </div>
        );
      })}

      {/* Prev / Next buttons */}
      {posts.length > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Previous"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white/40">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            onClick={next}
            aria-label="Next"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white/40">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      {/* Dots */}
      {posts.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {posts.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              aria-label={`Slide ${index + 1}`}
              className={`transition-all duration-300 rounded-full ${
                index === current ? 'w-6 h-2.5 bg-[#ffd600]' : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      )}

      {/* Progress bar */}
      {posts.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 z-20 h-0.5 bg-white/20">
          <div key={current} className="h-full bg-[#ffd600] animate-progress" style={{ animation: 'progress 5s linear forwards' }} />
        </div>
      )}

      <style>{`
        @keyframes progress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
        .animate-progress {
          width: 0%;
        }
      `}</style>
    </div>
  );
};

export default FeaturedEvent;
